import React, { useEffect, useState } from "react";
import { api } from "@/lib/api";
import { useApp } from "@/contexts/AppContext";
import { formatSAR } from "@/lib/i18n";
import { CalendarDays, Check, X, Clock } from "lucide-react";
import { Button } from "@/components/ui/button";
import { Badge } from "@/components/ui/badge";
import {
  DropdownMenu,
  DropdownMenuContent,
  DropdownMenuItem,
  DropdownMenuTrigger,
} from "@/components/ui/dropdown-menu";
import { toast } from "sonner";

export default function Bookings() {
  const { t, lang } = useApp();
  const [bookings, setBookings] = useState([]);
  const [loading, setLoading] = useState(true);

  const fetchData = async () => {
    setLoading(true);
    try {
      const { data } = await api.get("/bookings");
      setBookings(data || []);
    } catch (e) {
      toast.error("Failed to load");
    }
    setLoading(false);
  };

  useEffect(() => {
    fetchData();
  }, []);

  const setStatus = async (id, status) => {
    try {
      await api.patch(`/bookings/${id}/status`, { status });
      toast.success(lang === "ar" ? "تم التحديث" : "Updated");
      fetchData();
    } catch (e) {
      toast.error("Failed");
    }
  };

  const badge = (status) => {
    const map = {
      confirmed: "border-success/30 text-success bg-success/5",
      pending: "border-warning/30 text-warning bg-warning/10",
      cancelled: "border-destructive/30 text-destructive bg-destructive/10",
      completed: "border-primary/30 text-primary bg-accent",
    };
    return map[status] || "";
  };

  return (
    <div className="space-y-6">
      <div>
        <p className="text-xs uppercase tracking-widest text-muted-foreground font-bold">{t.dashboard}</p>
        <h1 className="text-2xl sm:text-3xl font-bold mt-1 flex items-center gap-2">
          <CalendarDays className="h-6 w-6 text-primary" />
          {t.bookingsList}
        </h1>
      </div>

      <div className="bg-surface border border-border rounded-xl overflow-x-auto">
        <table className="w-full text-sm">
          <thead className="bg-muted/50">
            <tr>
              <Th>{t.bookingNumber}</Th>
              <Th>{t.bookingCustomer}</Th>
              <Th>{t.bookingDate}</Th>
              <Th>{t.bookingTime}</Th>
              <Th>{t.bookingDuration}</Th>
              <Th>{t.total}</Th>
              <Th>{t.status}</Th>
              <Th className="text-end">{t.actions}</Th>
            </tr>
          </thead>
          <tbody>
            {loading && (
              <tr><td colSpan={8} className="text-center py-10 text-muted-foreground">Loading...</td></tr>
            )}
            {!loading && bookings.length === 0 && (
              <tr><td colSpan={8} className="text-center py-10 text-muted-foreground" data-testid="no-bookings">
                {t.noBookings}
              </td></tr>
            )}
            {bookings.map((b) => (
              <tr key={b.id} data-testid={`booking-row-${b.id}`} className="border-t border-border hover:bg-muted/30">
                <Td className="font-mono font-semibold">{b.booking_number}</Td>
                <Td>
                  <div className="font-medium">{b.customer_name}</div>
                  <div className="text-xs text-muted-foreground">{b.customer_mobile}</div>
                </Td>
                <Td>
                  <div>{new Date(b.booking_date).toLocaleDateString(lang === "ar" ? "ar-SA" : "en-GB")}</div>
                  <div className="text-xs text-muted-foreground capitalize">{t[b.weekday] || b.weekday}</div>
                </Td>
                <Td className="font-mono">{b.slot_start}</Td>
                <Td><span className="inline-flex items-center gap-1"><Clock className="h-3 w-3 text-muted-foreground" /> {b.duration_minutes} min</span></Td>
                <Td className="font-semibold">{formatSAR(b.total, lang)}</Td>
                <Td>
                  <Badge variant="outline" className={badge(b.status)}>{t[b.status] || b.status}</Badge>
                </Td>
                <Td className="text-end">
                  <DropdownMenu>
                    <DropdownMenuTrigger asChild>
                      <Button variant="ghost" size="sm" data-testid={`booking-actions-${b.id}`}>...</Button>
                    </DropdownMenuTrigger>
                    <DropdownMenuContent align="end">
                      <DropdownMenuItem onClick={() => setStatus(b.id, "confirmed")} data-testid={`booking-confirm-${b.id}`}><Check className="h-4 w-4 me-2" />{t.confirmed}</DropdownMenuItem>
                      <DropdownMenuItem onClick={() => setStatus(b.id, "completed")} data-testid={`booking-complete-${b.id}`}><Check className="h-4 w-4 me-2" />{t.completed}</DropdownMenuItem>
                      <DropdownMenuItem onClick={() => setStatus(b.id, "cancelled")} className="text-destructive" data-testid={`booking-cancel-${b.id}`}><X className="h-4 w-4 me-2" />{t.cancelled}</DropdownMenuItem>
                    </DropdownMenuContent>
                  </DropdownMenu>
                </Td>
              </tr>
            ))}
          </tbody>
        </table>
      </div>
    </div>
  );
}

const Th = ({ children, className = "" }) => (
  <th className={`text-start font-medium text-muted-foreground py-3 px-4 text-xs uppercase tracking-wider ${className}`}>{children}</th>
);
const Td = ({ children, className = "" }) => <td className={`py-3 px-4 align-middle ${className}`}>{children}</td>;
